import { Async } from '@client/new/helpers/types';
import { Folders, toFolder, toVideo, Videos } from './library.domain';
import {
  AddFolderMutation,
  AddToFolderMutation,
  DeleteFolderMutation,
  RenameFolderMutation,
  useAddFolderMutation,
  useAddToFolderMutation,
  useDeleteFolderMutation,
  useGetLibraryContentQuery,
  useRenameFolderMutation,
} from 'generated/graphql';
import { FetchResult } from '@apollo/client';

export type LibraryModelError = { reload: () => void };

export type LibraryModel = Async<
  {
    videos: Videos;
    folders: Folders;
    refetchLibraryContent: () => void;
    addToFolder: ({
      id,
      folderIds,
      videoIds,
    }: {
      id: number | null;
      folderIds?: number[];
      videoIds?: string[];
    }) => Promise<FetchResult<AddToFolderMutation>>;
    addFolder: (p: {
      name: string;
      parentId?: number;
    }) => Promise<FetchResult<AddFolderMutation>>;
    renameFolder: (p: {
      name: string;
      id: number;
    }) => Promise<FetchResult<RenameFolderMutation>>;
    deleteFolder: (p: {
      id: number;
    }) => Promise<FetchResult<DeleteFolderMutation>>;
  },
  LibraryModelError
>;

export const useLibraryModel = ({
  creatorId,
}: {
  creatorId: number;
}): LibraryModel => {
  const { data, loading, error, refetch } = useGetLibraryContentQuery({
    variables: {
      creatorId,
    },
    fetchPolicy: 'network-only',
  });

  const [addFolderMutation] = useAddFolderMutation();
  const [addToFolderMutation] = useAddToFolderMutation();
  const [renameFolderMutation] = useRenameFolderMutation();
  const [deleteFolderMutation] = useDeleteFolderMutation();

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: {
        reload: () => refetch(),
      },
    };
  }

  const videos = !data?.getCreatorsMedia
    ? []
    : data?.getCreatorsMedia
        .map(toVideo)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );

  const folders = !data?.getCreatorsFolders
    ? []
    : data?.getCreatorsFolders
        .map(toFolder)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );

  return {
    type: 'fetched',
    value: {
      videos,
      folders,
      refetchLibraryContent: () => refetch(),
      addToFolder: ({ id, folderIds, videoIds }) =>
        addToFolderMutation({
          variables: {
            creatorId,
            id,
            folderIds,
            videoIds,
          },
        }),
      addFolder: ({ name, parentId }) =>
        addFolderMutation({
          variables: {
            creatorId,
            name,
            parentId,
          },
        }),
      renameFolder: ({ name, id }) =>
        renameFolderMutation({
          variables: {
            creatorId,
            name,
            id,
          },
        }),
      deleteFolder: ({ id }) =>
        deleteFolderMutation({
          variables: {
            id,
          },
        }),
    },
  };
};
